export enum UserRole {
  ADMIN = "tenant_admin",
  PLATFORM_ADMIN = "platform_admin",
  CREATOR = "creator",
  /**
   * Marker added next to ADMIN for a school_admin account: it gets everything a
   * tenant admin gets, except what lists this role as denied (e.g. /institutions).
   */
  SCHOOL_ADMIN = "school_admin",
}

export interface StudentInfo {
  name: string;
  firstName: string;
  lastName: string;
  board: string; // e.g., "CBSE", "ICSE", etc.
  class: string; // e.g., "Class 9"
  section: string; // e.g., "A"
  academicYear: string; // e.g., "2025-26"
  institute: string; // e.g., "Modern Public School"
}

export interface TenantInfo {
  id: string;
  name: string;
}

/** Institute details returned by GET api/v1/auth/me (`institute`). */
export interface InstituteInfo {
  instituteName: string;
  state: string;
  cityVillage: string;
  pincode: string;
  /** ISO date, e.g. "2026-04-01" */
  academicStart: string;
  /** ISO date, e.g. "2027-03-31" */
  academicEnd: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  roles: UserRole[];
  avatar?: string;
  createdAt: string;
  updatedAt: string;
  username: string;
  scopes: string[];
  profile: StudentInfo;
  tenant: TenantInfo;
  // Optional extras from GET api/v1/auth/me (best-effort, absent for other sources)
  instituteId?: string;
  institute?: InstituteInfo;
  /** Account status, e.g. "active" */
  status?: string;
  designation?: string;
  primarySubject?: string;
  medium?: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// API Response Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

// Auth Response Types
// Legacy envelope (tokens + user). Login no longer uses it (see auth.types.ts);
// it is still used by the register flow.
export interface LoginResponse {
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn?: number;
  };

  user: User;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  refreshExpiresIn?: number;
}

// Request Config Types
// common.types.ts
export interface RequestConfig {
  skipAuth?: boolean;
  skipRefresh?: boolean;
}
